import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Alert, Form, FormGroup, Label, Input, Button, Card, CardBody, Table } from 'reactstrap'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { tokenConfig } from '../utils/apiConfig'
import useDatePickerPortal from '../utils/useDatePickerPortal'
import EventDetails from '../components/EventRegister/EventDetails'
import TermsAndConditions from '../components/EventRegister/TermsAndConditions'
import AttendeeDetails from '../components/EventRegister/AttendeeDetails'
import UpcomingEventsGrid from '../components/EventRegister/UpcomingEventsGrid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight, faCalendarAlt, faLeaf, faTrashAlt, faUsers } from '@fortawesome/free-solid-svg-icons';

const toDate = str => str ? new Date(str.substring(0, 10) + 'T00:00:00') : null
const fromDate = d => d ? [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-') : ''

const EventRegister = props => {

    const datePickerPortal = useDatePickerPortal()
    const [name, setName] = useState('')
    const [phoneNumber, setPhoneNumber] = useState('')
    const [validId, setValidId] = useState('')
    const [dob, setDob] = useState('')
    const [email, setEmail] = useState('')
    const [events, setEvents] = useState([])
    const [eventsLoaded, setEventsLoaded] = useState(false)
    const [upcomingEventSessions, setUpcomingEventSessions] = useState([])
    const [guests, setGuests] = useState([])
    const [eventDetailsModal, setEventDetailsModal] = useState(false)
    const [termsAndConditionsModal, setTermsAndConditionsModal] = useState(false)
    const [futureContact, setFutureContact] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    const [indemnity, setIndemnity] = useState(false)

    const [selectedEventId, setSelectedEventId] = useState('')
    const [sessions, setSessions] = useState([])
    const [sessionId, setSessionId] = useState('')

    const [attendeeModal, setAttendeeModal] = useState(false)

    const userDetails = props.userDetails
    const [searchParams] = useSearchParams()

    //Getting all Events using axios
    useEffect(() => {
        getEvents()
    }, [])

    // Apply query-param pre-selection once events are loaded
    useEffect(() => {
        if (!eventsLoaded) return
        const eventId = searchParams.get('eventId')
        const sid = searchParams.get('sessionId')
        if (eventId) {
            setSelectedEventId(eventId)
            if (sid) setSessionId(sid)
            setTimeout(() => {
                document.querySelector('.event-register-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 200)
        }
    }, [eventsLoaded, searchParams])

    useEffect(() => {
        setEmail(userDetails ? userDetails.user.email : '')
        setName(userDetails ? userDetails.user.name : '')
        setGuests(userDetails?.user?.guests || [])
        setDob(userDetails ? userDetails.user.dob : '')
        setValidId(userDetails ? userDetails.user.validId : '')
        setPhoneNumber(userDetails ? userDetails.user.phoneNumber : '')
    }, [userDetails])

    useEffect(() => {
        if (selectedEventId) {
            const loadEventSessions = async () => {
                setSessions([])
                const date = new Date()
                const dayBefore = date.setDate(date.getDate() - 1)
                const event = events.find(({ _id }) => _id === selectedEventId)
                if (!event) return

                await axios.post('/api/event-sessions/getSessionsByEventId', { eventId: selectedEventId, filters: { status: 'Open', date: { $gt: new Date(dayBefore).toISOString() }, registrations: { $lt: event.capacity } } }, tokenConfig())
                    .then(res => setSessions(res.data))
                    .catch(err => console.log(err))
            }

            loadEventSessions()
        }
    }, [events, selectedEventId])

    const selectedEvent = useMemo(() => {
        return events.find(({ _id }) => _id === selectedEventId) || null
    }, [events, selectedEventId])

    const mainSiteHighlights = [
        {
            icon: faLeaf,
            title: 'Bukit Kiara Federal Park',
            description: 'FoBK frames Bukit Kiara as a major urban forest and green lung, so activities are designed around care, learning, and stewardship.',
        },
        {
            icon: faUsers,
            title: 'Citizen Science and Volunteering',
            description: 'The main site encourages supporters to help through biodiversity work, volunteer efforts, and broader community participation.',
        },
        {
            icon: faCalendarAlt,
            title: 'Programmes and Public Activities',
            description: 'Use this portal when FoBK opens a walk, survey, talk, or other programme that needs confirmed registrations.',
        },
    ]

    const toggleAttendeeDetails = () => {
        setAttendeeModal(!attendeeModal)
    }

    const addGuest = (guest) => {
        setGuests([...guests, guest])
    }

    const deleteGuest = (index) => {
        const confirm = window.confirm("Remove Attendee?")
        if (confirm) {
            setGuests(guests.filter((e, i) => index !== i))
        }
    }

    const getEvents = async () => {
        try {
            const res = await axios.post('/api/events/filter', { filter: { status: 'Open' } })
            const allEvents = res.data

            const now = new Date()
            const dayBefore = new Date(now)
            dayBefore.setDate(dayBefore.getDate() - 1)
            const in3Months = new Date(now)
            in3Months.setMonth(in3Months.getMonth() + 3)

            const sessionResults = await Promise.all(
                allEvents.map(event =>
                    axios.post('/api/event-sessions/getSessionsByEventId', {
                        eventId: event._id,
                        filters: {
                            status: 'Open',
                            date: { $gt: dayBefore.toISOString(), $lte: in3Months.toISOString() },
                            registrations: { $lt: event.capacity },
                        }
                    }, tokenConfig())
                        .then(r => r.data)
                        .catch(() => [])
                )
            )

            const withSessions = allEvents
                .map((event, i) => ({ event, sessions: sessionResults[i] }))
                .filter(({ sessions }) => sessions.length > 0)

            setEvents(withSessions.map(({ event }) => event))
            setUpcomingEventSessions(withSessions)
            setEventsLoaded(true)
        } catch (err) {
            console.log(err)
        }
    }

    const toggleEventDetails = () => {
        setEventDetailsModal(!eventDetailsModal)
    }

    const toggleTermsAndConditions = () => {
        setTermsAndConditionsModal(!termsAndConditionsModal)
    }

    const acceptTermsAndConditions = () => {
        setIndemnity(true)
        setTermsAndConditionsModal(!termsAndConditionsModal)
    }

    const registerEvent = async e => {
        e.preventDefault()
        setErrorMessage('')
        setSuccessMessage('')
        if (!indemnity) {
            setErrorMessage('Required to Accept to Terms & Conditions')
        }
        const event = events.find(({ _id }) => _id === selectedEventId)

        const newRegistration = {
            event_id: selectedEventId,
            sessionId,
            futureContact,
            email,
            name,
            phoneNumber,
            validId,
            dob,
            guests,
            capacity: event.capacity
        }
        if (userDetails) {
            newRegistration.user_id = userDetails.user._id
        }
        await axios.post('/api/registrations', newRegistration)
            .then(async () => {
                setSuccessMessage('Registered, await confirmation email')
                setSelectedEventId('')
                setSessionId('')
                axios.patch('/api/event-sessions/registration', { ...newRegistration, registrationCount: guests.length + 1 })
                    .then(() => {
                        window.scrollTo(0, 0)
                    })
                    .catch(err => {
                        setErrorMessage(err.response.data.message)
                        window.scrollTo(0, 0)
                    })
            })
            .catch(err => {
                setErrorMessage(err.response.data.message)
                window.scrollTo(0, 0)
            })

    }
    //Calling Backend using GraphQL
    return (
        <div className='page-shell page-shell--wide page-section'>
            <EventDetails
                data={events}
                eventId={selectedEventId}
                modal={eventDetailsModal}
                toggleEventDetails={toggleEventDetails} />
            <TermsAndConditions
                modal={termsAndConditionsModal}
                toggle={toggleTermsAndConditions}
                acceptTermsAndConditions={acceptTermsAndConditions}
            />
            <AttendeeDetails
                modal={attendeeModal}
                toggle={toggleAttendeeDetails}
                addGuest={addGuest}
            />

            <div className='page-header'>
                <div>
                    <span className='eyebrow'>Event Registration</span>
                    <h1 className='section-title'>Register for FoBK walks, programmes, and community activities</h1>
                    <p className='section-lead'>
                        FoBK.org highlights hiking in Bukit Kiara, learning about the federal park, and getting involved in citizen-science and conservation. Use this portal to secure a place whenever an FoBK activity opens for registration.
                    </p>
                </div>
                <div className='page-header__meta'>
                    <div className='metric-card'>
                        <span>Open Events</span>
                        <strong>{events.length}</strong>
                    </div>
                    <div className='metric-card'>
                        <span>Available Sessions</span>
                        <strong>{sessions.length}</strong>
                    </div>
                    <div className='metric-card'>
                        <span>Participants</span>
                        <strong>{guests.length + 1}</strong>
                    </div>
                </div>
            </div>

            {eventsLoaded && upcomingEventSessions.length > 0 && (
                <div style={{ marginBottom: '2.5rem' }}>
                    <span className='eyebrow'>What's On</span>
                    <h2 className='section-title' style={{ marginTop: '0.2rem', marginBottom: '1.25rem' }}>Upcoming events</h2>
                    <UpcomingEventsGrid
                        upcomingEventSessions={upcomingEventSessions}
                        onSelectSession={(event, session) => {
                            setSelectedEventId(event._id)
                            setSessionId(session._id)
                            document.querySelector('.event-register-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }}
                    />
                </div>
            )}

            <div className='event-register-grid'>
                <Card className='fobk-surface-card'>
                    <CardBody>
                        {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
                        {successMessage && <Alert color="success">{successMessage}</Alert>}
                        <Form onSubmit={e => registerEvent(e)}>
                            <FormGroup>
                                <Label>Full Name as per ID*</Label>
                                <Input
                                    type="text"
                                    placeholder="Your Name"
                                    defaultValue={userDetails ? userDetails.user.name : name}
                                    onChange={e => setName(e.target.value)}
                                    required />
                            </FormGroup>
                            <FormGroup>
                                <Label>Email*</Label>
                                <Input
                                    type="email"
                                    placeholder="Your Email"
                                    defaultValue={userDetails ? userDetails.user.email : email}
                                    onChange={e => setEmail(e.target.value)}
                                    required />
                            </FormGroup>
                            <FormGroup>
                                <Label>Passport/IC*</Label>
                                <Input
                                    type="text"
                                    placeholder="Provide Valid ID"
                                    defaultValue={userDetails ? userDetails.user.validId : validId}
                                    onChange={e => setValidId(e.target.value)}
                                    required
                                />
                                <p className='form-note'>Information is required for insurance purposes. All data will remain private and confidential.</p>
                            </FormGroup>
                            <FormGroup>
                                <Label>Date of Birth*</Label>
                                <DatePicker
                                    selected={toDate(dob)}
                                    onChange={d => setDob(fromDate(d))}
                                    dateFormat="dd/MM/yyyy"
                                    maxDate={new Date()}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={100}
                                    className="form-control"
                                    wrapperClassName="d-block"
                                    placeholderText="Select date of birth"
                                    {...datePickerPortal}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Phone Number*</Label>
                                <Input
                                    type="text"
                                    placeholder="Your Phone Number"
                                    defaultValue={userDetails ? userDetails.user.phoneNumber : phoneNumber}
                                    onChange={e => setPhoneNumber(e.target.value)}
                                    required
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Additional Attendees</Label>
                                {guests?.length > 0 && <div className='table-shell'>
                                    <Table className='fobk-data-table'>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>D.O.B</th>
                                                <th>ID</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {guests.map((guest, i) => <tr key={`${guest.validId}-${i}`}>
                                                <td>{guest.name}</td>
                                                <td>{new Date(guest.dob).toLocaleDateString()}</td>
                                                <td>{guest.validId}</td>
                                                <td>
                                                    <span
                                                        style={{ cursor: 'pointer', color: 'red' }}
                                                        onClick={() => deleteGuest(i)}
                                                    >
                                                        <FontAwesomeIcon icon={faTrashAlt} />
                                                    </span>
                                                </td>
                                            </tr>)}
                                        </tbody>
                                    </Table>
                                </div>}
                                <div style={{ display: 'block', marginTop: '0.85rem' }}>
                                    <Button color='secondary' type='button' onClick={toggleAttendeeDetails}>Add Attendee</Button>
                                </div>
                            </FormGroup>
                            {eventsLoaded && events.length === 0 ? (
                                <p style={{ color: '#6c757d', fontStyle: 'italic', margin: '0.5rem 0 1rem' }}>
                                    There are no open events available at this time.
                                </p>
                            ) : (<>
                            <FormGroup>
                                <Label for="EventName">Event*</Label>
                                <Input
                                    type="select"
                                    value={selectedEventId}
                                    onChange={e => {
                                        setSelectedEventId(e.target.value)
                                        setSessionId('')
                                    }}
                                    required >
                                    <option value="">Select Event</option>
                                    {events.map(({ _id, name }) => {
                                        return <option key={_id} value={_id}>{name}</option>
                                    })}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label for="EventDate">Session*</Label>
                                <Input
                                    type="select"
                                    value={sessionId}
                                    onChange={e => setSessionId(e.target.value)}
                                    required>
                                    <option value="">{!selectedEventId ? "Requires Event" : "Select a Date"}</option>
                                    {sessions.map(({ _id, date, starttime, endtime }) =>
                                        <option key={_id} value={_id}>{date.substr(0, 10)}, {starttime}-{endtime}</option>
                                    )}
                                </Input>
                            </FormGroup>
                            </>)}
                            <FormGroup check>
                                <Label check>
                                    <Input type="checkbox" defaultChecked={futureContact} onChange={e => setFutureContact(e.target.checked)} />{' '}
                                    Keep me updated on future events
                                </Label>
                            </FormGroup>
                            <FormGroup check style={{ marginBottom: '0.9rem' }}>
                                <Label check>
                                    <Input type="checkbox" checked={indemnity} onChange={e => setIndemnity(e.target.checked)} required />{' '}
                                    I accept the{' '}
                                    <button className='text-button' type='button' onClick={toggleTermsAndConditions}>
                                        Terms and Conditions
                                    </button>
                                </Label>
                            </FormGroup>
                            <div className='hero-actions'>
                                <Button color='primary'>Submit Registration</Button>
                                <Button color='dark' type='button' onClick={toggleEventDetails}>
                                    Event Details
                                </Button>
                            </div>
                            <div style={{ marginTop: '1rem' }}>
                                <a href="https://docs.google.com/document/d/1ThQNAsybXakeZpoqaG5OOs2zV0Z9i5njMpwCCbUtveg/edit?usp=sharing" target="_blank" rel='noreferrer' style={{ fontSize: "0.85rem" }}>
                                    Privacy Policy
                                </a>
                            </div>
                        </Form>
                    </CardBody>
                </Card>

                <div className='sidebar-stack'>
                    <div className='fobk-surface-card fobk-surface-card--soft'>
                        <span className='eyebrow'>Selected Event</span>
                        <h2 className='section-title' style={{ fontSize: '1.6rem' }}>
                            {selectedEvent?.name || 'Choose an event to preview it here'}
                        </h2>
                        <p className='section-lead'>
                            {selectedEvent?.description || 'Event details, timing, and capacity cues will appear here once you pick an activity.'}
                        </p>
                        <div className='meta-pills'>
                            {selectedEvent?.difficulty && <span className='data-pill'>{selectedEvent.difficulty}</span>}
                            {selectedEvent?.capacity && <span className='data-pill'>{selectedEvent.capacity} slots</span>}
                            {sessions.length > 0 && <span className='data-pill'>{sessions.length} sessions</span>}
                        </div>
                    </div>

                    <div className='fobk-surface-card'>
                        <span className='eyebrow'>From FoBK.org</span>
                        <div className='info-list'>
                            {mainSiteHighlights.map(({ icon, title, description }) => (
                                <div className='info-list__item info-list__item--stacked' key={title}>
                                    <strong>
                                        <FontAwesomeIcon icon={icon} style={{ marginRight: '0.55rem' }} />
                                        {title}
                                    </strong>
                                    <span>{description}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='fobk-surface-card fobk-surface-card--dark'>
                        <span className='eyebrow'>Main Site</span>
                        <h3 style={{ marginTop: '1rem', fontSize: '1.45rem', fontWeight: 700 }}>Continue on FoBK.org</h3>
                        <p style={{ marginTop: '0.85rem' }}>
                            Use the main site for the broader FoBK story: Bukit Kiara background, FoBK history, programme context, and ways to support the organisation beyond event registration.
                        </p>
                        <div className='info-list'>
                            <div className='info-list__item'>
                                <strong>Learn More About Bukit Kiara</strong>
                                <span>Explore why the federal park matters to the city.</span>
                            </div>
                            <div className='info-list__item'>
                                <strong>See FoBK Programmes</strong>
                                <span>Browse the organisation's public-facing programmes and activities.</span>
                            </div>
                            <div className='info-list__item'>
                                <strong>Get Involved or Donate</strong>
                                <span>Find ways to volunteer, become a member, or support the movement.</span>
                            </div>
                        </div>
                        <div className='hero-actions'>
                            <a
                                className='btn btn-outline-light'
                                target='_blank'
                                rel='noreferrer'
                                href="https://www.fobk.org/"
                            >
                                Visit FoBK.org
                                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.55rem' }} />
                            </a>
                            <a
                                className='btn btn-outline-light'
                                target='_blank'
                                rel='noreferrer'
                                href="https://www.fobk.org/get-involved/"
                            >
                                Get Involved
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EventRegister
